import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ShoppingBag, User, Command, Menu, X, LayoutDashboard, Shield, LogOut } from "lucide-react";
import { Logo } from "../brand/Logo";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";

const NAV_LINKS = [
    { key: "domains", href: "/home-a#domains" },
    { key: "dns", href: "/home-a#dns" },
    { key: "email", href: "/home-a#email" },
    { key: "pricing", href: "/home-a#pricing" },
    { key: "support", href: "/home-a#faq" },
];

export const TopNav = ({ onCommandOpen, onCartOpen, cartCount = 0 }) => {
    const { t, i18n } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const loc = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

    const handleNavClick = (e, href) => {
        const [path, hash] = href.split("#");
        // If we're already on the same route, scroll to the anchor manually
        // (React Router does not re-fire effects on a no-op navigation).
        if (loc.pathname === path && hash) {
            e.preventDefault();
            setMobileOpen(false);
            const el = document.getElementById(hash);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                // Keep the URL in sync without adding a new history entry
                if (loc.hash !== `#${hash}`) {
                    window.history.replaceState(null, "", `${path}#${hash}`);
                }
            }
        }
    };

    const toggleLang = () => {
        i18n.changeLanguage(i18n.language === "ar" ? "en" : "ar");
    };

    const isMac =
        typeof navigator !== "undefined" &&
        /Mac|iPhone|iPad/.test(navigator.platform || "");

    return (
        <header
            data-testid="top-nav"
            className={cn(
                "fixed inset-x-0 top-0 z-40 transition-all duration-200 ease-nour",
                scrolled
                    ? "bg-limestone-100/92 backdrop-blur-xl border-b-2 border-ink-200"
                    : "bg-limestone-100 border-b-2 border-transparent",
            )}
        >
            <div className="mx-auto flex h-[72px] max-w-[1320px] items-center gap-6 px-5 lg:px-8">
                <Link to="/preview" className="flex items-center" data-testid="nav-home-link">
                    <Logo size={32} />
                </Link>

                <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.key}
                            to={link.href}
                            onClick={(e) => handleNavClick(e, link.href)}
                            data-testid={`nav-link-${link.key}`}
                            className={({ isActive }) =>
                                cn(
                                    "rounded-btn px-4 py-2.5 text-[16px] font-semibold text-ink-700 transition-colors duration-200 hover:bg-mudbrick-200 hover:text-ink-800",
                                    isActive && "text-ink-800 bg-mudbrick-200",
                                )
                            }
                        >
                            {t(`nav.${link.key}`)}
                        </NavLink>
                    ))}
                </nav>

                <div className="ms-auto flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={onCommandOpen}
                        data-testid="nav-command-trigger"
                        className="hidden md:inline-flex h-12 items-center gap-2 rounded-btn border-2 border-ink-200 bg-white px-4 text-[14px] font-semibold text-ink-600 transition-colors duration-200 hover:border-ink-800 hover:text-ink-800"
                        aria-label={t("nav.openCommand")}
                    >
                        <Search className="h-4 w-4" />
                        <span>{t("nav.search")}</span>
                        <span className="ms-2 hidden md:inline-flex items-center gap-0.5 rounded-[4px] border border-ink-200 bg-limestone-200 px-1.5 py-0.5 font-mono text-[11px] text-ink-600 tabular-nums num-ltr">
                            {isMac ? <><Command className="h-3 w-3" />K</> : "Ctrl K"}
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={onCommandOpen}
                        aria-label={t("nav.search")}
                        data-testid="nav-command-trigger-mobile"
                        className="inline-flex h-12 w-12 items-center justify-center rounded-btn text-ink-700 transition-colors duration-200 hover:bg-mudbrick-200 md:hidden"
                    >
                        <Search className="h-5 w-5" />
                    </button>

                    <button
                        type="button"
                        onClick={toggleLang}
                        data-testid="nav-lang-toggle"
                        className="inline-flex h-12 items-center rounded-btn px-4 text-[14px] font-bold text-ink-800 transition-colors duration-200 hover:bg-mudbrick-200"
                        aria-label="Toggle language"
                    >
                        {t("nav.toggleLang")}
                    </button>

                    <button
                        type="button"
                        onClick={onCartOpen}
                        data-testid="nav-cart-trigger"
                        className="relative inline-flex h-12 w-12 items-center justify-center rounded-btn text-ink-800 transition-colors duration-200 hover:bg-mudbrick-200"
                        aria-label={t("nav.cart")}
                    >
                        <ShoppingBag className="h-5 w-5" />
                        {cartCount > 0 && (
                            <span className="absolute end-1 top-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-madder-500 px-1 text-[11px] font-bold text-white tabular-nums num-ltr">
                                {cartCount}
                            </span>
                        )}
                    </button>

                    <Link
                        to={user?.role === "admin" ? "/admin" : "/dashboard"}
                        data-testid="nav-dashboard-link"
                        className={`hidden md:inline-flex h-12 items-center gap-2 rounded-btn border-2 border-ink-800 bg-transparent px-4 text-[15px] font-bold text-ink-800 transition-all duration-200 hover:bg-ink-800 hover:text-limestone-100 ${user ? "" : "hidden md:hidden"}`}
                    >
                        {user?.role === "admin" ? <Shield className="me-1.5 h-4 w-4" /> : <LayoutDashboard className="me-1.5 h-4 w-4" />}
                        {user?.role === "admin" ? "Admin" : "Dashboard"}
                    </Link>

                    {!user && (
                        <Link
                            to="/login"
                            data-testid="nav-sign-in"
                            className="hidden md:inline-flex h-12 items-center rounded-btn border-2 border-ink-800 bg-transparent px-4 text-[15px] font-bold text-ink-800 transition-all duration-200 hover:bg-ink-800 hover:text-limestone-100"
                        >
                            <User className="me-1.5 h-4 w-4" />
                            {t("nav.signIn")}
                        </Link>
                    )}

                    {user ? (
                        <button
                            type="button"
                            onClick={logout}
                            data-testid="nav-logout"
                            className="hidden md:inline-flex h-12 items-center rounded-btn bg-madder-500 px-5 text-[15px] font-bold text-white transition-all duration-200 hover:bg-madder-600"
                        >
                            <LogOut className="me-1.5 h-4 w-4" />
                            Sign out
                        </button>
                    ) : (
                        <Link
                            to="/home-a#hero-search"
                            data-testid="nav-cta-primary"
                            className="hidden md:inline-flex h-12 items-center rounded-btn bg-madder-500 px-5 text-[15px] font-bold text-white transition-all duration-200 hover:bg-madder-600 active:translate-y-[1px]"
                        >
                            {t("cta.getStarted")}
                        </Link>
                    )}

                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <button
                                type="button"
                                aria-label={t("nav.menu")}
                                data-testid="nav-menu-trigger"
                                className="inline-flex h-12 w-12 items-center justify-center rounded-btn text-ink-800 transition-colors duration-200 hover:bg-mudbrick-200 lg:hidden"
                            >
                                <Menu className="h-5 w-5" />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="top"
                            className="h-full border-b-0 bg-limestone-100"
                            data-testid="nav-menu-sheet"
                        >
                            <div className="flex items-center justify-between pb-6">
                                <Logo size={30} />
                                <button
                                    type="button"
                                    onClick={() => setMobileOpen(false)}
                                    className="inline-flex h-12 w-12 items-center justify-center rounded-btn text-ink-800 hover:bg-mudbrick-200"
                                    aria-label="Close"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="flex flex-col gap-1">
                                {NAV_LINKS.map((link) => (
                                    <NavLink
                                        key={link.key}
                                        to={link.href}
                                        onClick={(e) => handleNavClick(e, link.href)}
                                        data-testid={`nav-link-mobile-${link.key}`}
                                        className="rounded-btn px-4 py-4 text-2xl font-bold text-ink-800 hover:bg-mudbrick-200"
                                    >
                                        {t(`nav.${link.key}`)}
                                    </NavLink>
                                ))}
                                <Link
                                    to="/login"
                                    className="mt-4 rounded-btn border-2 border-ink-800 px-4 py-4 text-center text-[16px] font-bold text-ink-800"
                                >
                                    {t("nav.signIn")}
                                </Link>
                                <Link
                                    to="/home-a#hero-search"
                                    className="rounded-btn bg-madder-500 px-4 py-4 text-center text-[16px] font-bold text-white"
                                >
                                    {t("cta.getStarted")}
                                </Link>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};
